import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db, functions } from './firebase';
import { ProfileRow } from './ProfileLink';
import { formatRentalDateRange } from './dateUtils';
import {
  formatPayoutDueLabel,
  getConditionHoldAmount,
  getOwnerPayoutSummary,
  PLATFORM_FEE_PERCENT,
} from './rentalPayments';
import {
  fetchPlatformFeeSettingsAdmin,
  savePlatformFeeSettingsAdmin,
  setOwnerFeeExemptAdmin,
  FOUNDING_LISTER_CAP_DEFAULT,
} from './platformFees';

const cardStyle = {
  padding: '24px',
  border: '1px solid #e5e7eb',
  borderRadius: '16px',
  backgroundColor: '#ffffff',
  boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
};

function formatMoney(amount) {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '—';
  return `$${value.toFixed(2)}`;
}

function HoldStatusBadge({ status }) {
  const styles = {
    authorized: { bg: '#fef3c7', color: '#92400e', label: 'Hold active' },
    captured: { bg: '#fee2e2', color: '#991b1b', label: 'Captured' },
    released: { bg: '#ecfdf5', color: '#047857', label: 'Released' },
    release_failed: { bg: '#fef2f2', color: '#b91c1c', label: 'Release failed' },
    failed: { bg: '#fef2f2', color: '#b91c1c', label: 'Authorization failed' },
    pending: { bg: '#f3f4f6', color: '#4b5563', label: 'Pending' },
    not_required: { bg: '#f3f4f6', color: '#6b7280', label: 'Not required' },
  };
  const style = styles[status] || { bg: '#f3f4f6', color: '#4b5563', label: status || 'Unknown' };

  return (
    <span
      style={{
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: '9999px',
        backgroundColor: style.bg,
        color: style.color,
        fontWeight: '700',
        fontSize: '0.82rem',
      }}
    >
      {style.label}
    </span>
  );
}

function ConditionHoldCard({ rental, onUpdated }) {
  const [captureAmount, setCaptureAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busyAction, setBusyAction] = useState('');
  const [message, setMessage] = useState('');

  const holdAmount = getConditionHoldAmount(null, rental);
  const maxCapture = holdAmount;

  const handleCapture = async () => {
    const amount = captureAmount.trim() ? Number(captureAmount) : holdAmount;
    if (!Number.isFinite(amount) || amount <= 0) {
      setMessage('Enter a valid capture amount.');
      return;
    }
    if (amount > maxCapture) {
      setMessage(`Capture cannot exceed ${formatMoney(maxCapture)}.`);
      return;
    }
    if (!window.confirm(`Capture ${formatMoney(amount)} from this renter's condition hold?`)) {
      return;
    }

    setBusyAction('capture');
    setMessage('');
    try {
      const captureHold = httpsCallable(functions, 'captureRentalConditionHold');
      await captureHold({
        rentalId: rental.id,
        amount,
        reason: reason.trim(),
      });
      setMessage(`Captured ${formatMoney(amount)} successfully.`);
      setCaptureAmount('');
      setReason('');
      onUpdated?.();
    } catch (error) {
      setMessage(error.message || 'Could not capture hold.');
    } finally {
      setBusyAction('');
    }
  };

  const handleRelease = async () => {
    if (!window.confirm('Release this condition hold without charging the renter?')) {
      return;
    }

    setBusyAction('release');
    setMessage('');
    try {
      const releaseHold = httpsCallable(functions, 'releaseRentalConditionHold');
      await releaseHold({ rentalId: rental.id, reason: reason.trim() });
      setMessage('Hold released successfully.');
      setReason('');
      onUpdated?.();
    } catch (error) {
      setMessage(error.message || 'Could not release hold.');
    } finally {
      setBusyAction('');
    }
  };

  return (
    <div style={{ ...cardStyle, marginBottom: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <h4 style={{ margin: '0 0 8px 0' }}>
            {rental.gearId ? (
              <Link to={`/gear/${rental.gearId}`} style={{ color: '#1f2937', textDecoration: 'none' }}>
                {rental.gearName || 'Gear rental'}
              </Link>
            ) : (
              rental.gearName || 'Gear rental'
            )}
          </h4>
          <p style={{ margin: '0 0 6px 0', color: '#6b7280', fontSize: '0.92rem' }}>
            Rental ID: <code>{rental.id}</code>
          </p>
          <p style={{ margin: 0, color: '#374151' }}>
            {formatRentalDateRange(rental.rentalStartDate, rental.rentalEndDate)}
          </p>
        </div>
        <HoldStatusBadge status={rental.conditionHoldStatus} />
      </div>

      <div style={{ marginTop: '16px', display: 'grid', gap: '8px', fontSize: '0.95rem' }}>
        <ProfileRow label="Renter" userId={rental.renterId} />
        <ProfileRow label="Owner" userId={rental.ownerId} />
        <p style={{ margin: 0 }}>
          <strong>Rental paid:</strong> {formatMoney((rental.totalPaidCents || rental.totalAmount * 100) / 100)}
        </p>
        <p style={{ margin: 0 }}>
          <strong>Condition hold:</strong> {formatMoney(holdAmount)}
          {rental.conditionHoldCapturedCents > 0 && (
            <> · Captured: {formatMoney(rental.conditionHoldCapturedCents / 100)}</>
          )}
        </p>
        <p style={{ margin: 0 }}>
          <strong>Owner payout:</strong>{' '}
          {rental.payoutStatus === 'transferred'
            ? `${formatMoney(getOwnerPayoutSummary(rental)?.ownerAmount)} sent`
            : rental.payoutStatus === 'pending_owner_stripe'
              ? 'Waiting for owner Stripe connection'
              : rental.payoutStatus === 'held'
                ? `Scheduled around ${formatPayoutDueLabel(rental) || '—'}`
                : rental.payoutStatus || '—'}
        </p>
      </div>

      {rental.conditionHoldStatus === 'authorized' && (
        <div
          style={{
            marginTop: '20px',
            padding: '16px',
            borderRadius: '12px',
            backgroundColor: '#fffbeb',
            border: '1px solid #fcd34d',
          }}
        >
          <p style={{ margin: '0 0 12px 0', fontWeight: '700', color: '#92400e' }}>
            Damage / condition action
          </p>
          <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '0.9rem' }}>
            Capture amount (leave blank for full {formatMoney(holdAmount)})
          </label>
          <input
            type="number"
            min="0.01"
            max={maxCapture}
            step="0.01"
            value={captureAmount}
            onChange={(e) => setCaptureAmount(e.target.value)}
            placeholder={holdAmount.toFixed(2)}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '10px',
              border: '1px solid #d1d5db',
              marginBottom: '12px',
              boxSizing: 'border-box',
            }}
          />
          <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '0.9rem' }}>
            Notes (optional)
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Brief note about damage or condition issue..."
            style={{
              width: '100%',
              minHeight: '72px',
              padding: '12px',
              borderRadius: '10px',
              border: '1px solid #d1d5db',
              marginBottom: '12px',
              boxSizing: 'border-box',
              resize: 'vertical',
            }}
          />
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handleCapture}
              disabled={Boolean(busyAction)}
              style={{
                padding: '10px 18px',
                backgroundColor: busyAction ? '#9ca3af' : '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '700',
                cursor: busyAction ? 'not-allowed' : 'pointer',
              }}
            >
              {busyAction === 'capture' ? 'Capturing...' : 'Capture hold'}
            </button>
            <button
              type="button"
              onClick={handleRelease}
              disabled={Boolean(busyAction)}
              style={{
                padding: '10px 18px',
                backgroundColor: busyAction ? '#9ca3af' : '#059669',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '700',
                cursor: busyAction ? 'not-allowed' : 'pointer',
              }}
            >
              {busyAction === 'release' ? 'Releasing...' : 'Release hold (no charge)'}
            </button>
          </div>
        </div>
      )}

      {message && (
        <p style={{ margin: '12px 0 0 0', color: message.includes('success') ? '#047857' : '#b45309' }}>
          {message}
        </p>
      )}
    </div>
  );
}

function toLocalInputValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInputValue(local) {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function PlatformFeeSettingsCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [effectiveFee, setEffectiveFee] = useState(PLATFORM_FEE_PERCENT);
  const [promoActive, setPromoActive] = useState(false);
  const [defaultFee, setDefaultFee] = useState(String(PLATFORM_FEE_PERCENT));
  const [promoFee, setPromoFee] = useState('');
  const [promoStart, setPromoStart] = useState('');
  const [promoEnd, setPromoEnd] = useState('');
  const [promoLabel, setPromoLabel] = useState('');
  const [foundingCount, setFoundingCount] = useState(0);
  const [foundingCap, setFoundingCap] = useState(FOUNDING_LISTER_CAP_DEFAULT);
  const [exemptUserId, setExemptUserId] = useState('');
  const [exemptBusy, setExemptBusy] = useState(false);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setMessage('');
    try {
      const data = await fetchPlatformFeeSettingsAdmin();
      const s = data.settings || {};
      setDefaultFee(String(s.defaultPlatformFeePercent ?? PLATFORM_FEE_PERCENT));
      setPromoFee(
        s.promoFeePercent === null || s.promoFeePercent === undefined
          ? ''
          : String(s.promoFeePercent)
      );
      setPromoStart(toLocalInputValue(s.promoStartsAt));
      setPromoEnd(toLocalInputValue(s.promoEndsAt));
      setPromoLabel(s.promoLabel || '');
      setFoundingCount(s.foundingListerCount || 0);
      setFoundingCap(s.foundingListerCap || FOUNDING_LISTER_CAP_DEFAULT);
      setEffectiveFee(data.effectivePlatformFeePercent ?? PLATFORM_FEE_PERCENT);
      setPromoActive(Boolean(data.promoActive));
    } catch (error) {
      setMessage(error.message || 'Could not load fee settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      const data = await savePlatformFeeSettingsAdmin({
        defaultPlatformFeePercent: Number(defaultFee),
        promoFeePercent: promoFee === '' ? null : Number(promoFee),
        promoStartsAt: fromLocalInputValue(promoStart),
        promoEndsAt: fromLocalInputValue(promoEnd),
        promoLabel: promoLabel.trim(),
        foundingListerCap: Number(foundingCap),
      });
      setEffectiveFee(data.effectivePlatformFeePercent);
      setPromoActive(Boolean(data.promoActive));
      setFoundingCount(data.settings?.foundingListerCount || 0);
      setMessage('Fee settings saved.');
    } catch (error) {
      setMessage(error.message || 'Could not save fee settings.');
    } finally {
      setSaving(false);
    }
  };

  const handlePauseAllFees = async () => {
    if (
      !window.confirm(
        'Set platform fee to 0% for ALL non-founding-only... actually for EVERYONE (global fee 0%) until you change it back? Founding owners already pay 0%.'
      )
    ) {
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      const data = await savePlatformFeeSettingsAdmin({
        defaultPlatformFeePercent: 0,
        clearPromo: true,
      });
      setDefaultFee('0');
      setPromoFee('');
      setPromoStart('');
      setPromoEnd('');
      setPromoLabel('');
      setEffectiveFee(data.effectivePlatformFeePercent);
      setPromoActive(false);
      setMessage('Platform fee paused (0%) for everyone. Remember to restore 10% when the promo ends.');
    } catch (error) {
      setMessage(error.message || 'Could not pause fees.');
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreDefaultFee = async () => {
    setSaving(true);
    setMessage('');
    try {
      const data = await savePlatformFeeSettingsAdmin({
        defaultPlatformFeePercent: PLATFORM_FEE_PERCENT,
        clearPromo: true,
      });
      setDefaultFee(String(PLATFORM_FEE_PERCENT));
      setPromoFee('');
      setPromoStart('');
      setPromoEnd('');
      setPromoLabel('');
      setEffectiveFee(data.effectivePlatformFeePercent);
      setPromoActive(false);
      setMessage(`Restored default platform fee to ${PLATFORM_FEE_PERCENT}%. Founding free-for-life owners still keep 100%.`);
    } catch (error) {
      setMessage(error.message || 'Could not restore fee.');
    } finally {
      setSaving(false);
    }
  };

  const handleClearPromo = async () => {
    setSaving(true);
    setMessage('');
    try {
      const data = await savePlatformFeeSettingsAdmin({ clearPromo: true });
      setPromoFee('');
      setPromoStart('');
      setPromoEnd('');
      setPromoLabel('');
      setEffectiveFee(data.effectivePlatformFeePercent);
      setPromoActive(false);
      setMessage('Timed promo cleared. Default fee applies to non-exempt owners.');
    } catch (error) {
      setMessage(error.message || 'Could not clear promo.');
    } finally {
      setSaving(false);
    }
  };

  const handleExemptToggle = async (exempt) => {
    const userId = exemptUserId.trim();
    if (!userId) {
      setMessage('Enter a user ID (Firebase UID) to grant or revoke free-for-life.');
      return;
    }
    setExemptBusy(true);
    setMessage('');
    try {
      await setOwnerFeeExemptAdmin({
        userId,
        exempt,
        reason: exempt ? 'admin_grant' : '',
      });
      setMessage(
        exempt
          ? `User ${userId} is now fee-exempt (keeps 100%).`
          : `User ${userId} fee exemption removed (standard fee applies unless a promo is active).`
      );
    } catch (error) {
      setMessage(error.message || 'Could not update user exemption.');
    } finally {
      setExemptBusy(false);
    }
  };

  if (loading) {
    return (
      <div style={{ ...cardStyle, marginBottom: '36px' }}>
        <p style={{ margin: 0, color: '#6b7280' }}>Loading fee settings…</p>
      </div>
    );
  }

  return (
    <section style={{ marginBottom: '36px' }}>
      <h3 style={{ color: '#1f2937', marginBottom: '16px' }}>Platform fees & founding listers</h3>
      <div style={{ ...cardStyle }}>
        <p style={{ margin: '0 0 16px', color: '#6b7280', lineHeight: 1.55, fontSize: '0.95rem' }}>
          <strong>Effective fee right now for normal owners:</strong>{' '}
          <span style={{ color: '#0f172a', fontWeight: 700 }}>{effectiveFee}%</span>
          {promoActive ? ' (timed promo active)' : ''}. Founding / exempt owners always keep{' '}
          <strong>100%</strong>. First {foundingCap} people who publish a listing get free-for-life
          automatically ({foundingCount} used · {Math.max(0, foundingCap - foundingCount)} left).
        </p>

        <form onSubmit={handleSave} style={{ display: 'grid', gap: '14px', maxWidth: '520px' }}>
          <label style={{ display: 'grid', gap: '6px', fontWeight: 600, fontSize: '0.9rem' }}>
            Default platform fee % (normal days)
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              value={defaultFee}
              onChange={(e) => setDefaultFee(e.target.value)}
              style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db' }}
            />
          </label>

          <div
            style={{
              padding: '14px',
              borderRadius: '12px',
              backgroundColor: '#fffbeb',
              border: '1px solid #fde68a',
              display: 'grid',
              gap: '12px',
            }}
          >
            <strong style={{ color: '#92400e' }}>Timed promo (optional)</strong>
            <p style={{ margin: 0, color: '#78716c', fontSize: '0.88rem', lineHeight: 1.5 }}>
              Set a promo fee (e.g. 0 for free weekend, or 5 for half-ish). Use start/end times. Leave promo fee empty
              to use the default only.
            </p>
            <label style={{ display: 'grid', gap: '6px', fontWeight: 600, fontSize: '0.9rem' }}>
              Promo fee %
              <input
                type="number"
                min="0"
                max="100"
                step="1"
                placeholder="e.g. 0"
                value={promoFee}
                onChange={(e) => setPromoFee(e.target.value)}
                style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db' }}
              />
            </label>
            <label style={{ display: 'grid', gap: '6px', fontWeight: 600, fontSize: '0.9rem' }}>
              Promo label (optional)
              <input
                type="text"
                placeholder="e.g. Launch weekend"
                value={promoLabel}
                onChange={(e) => setPromoLabel(e.target.value)}
                style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db' }}
              />
            </label>
            <label style={{ display: 'grid', gap: '6px', fontWeight: 600, fontSize: '0.9rem' }}>
              Promo starts
              <input
                type="datetime-local"
                value={promoStart}
                onChange={(e) => setPromoStart(e.target.value)}
                style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db' }}
              />
            </label>
            <label style={{ display: 'grid', gap: '6px', fontWeight: 600, fontSize: '0.9rem' }}>
              Promo ends
              <input
                type="datetime-local"
                value={promoEnd}
                onChange={(e) => setPromoEnd(e.target.value)}
                style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db' }}
              />
            </label>
          </div>

          <label style={{ display: 'grid', gap: '6px', fontWeight: 600, fontSize: '0.9rem' }}>
            Founding free-for-life cap
            <input
              type="number"
              min={foundingCount}
              step="1"
              value={foundingCap}
              onChange={(e) => setFoundingCap(e.target.value)}
              style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db' }}
            />
            <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '0.85rem' }}>
              Cannot go below people already granted ({foundingCount}).
            </span>
          </label>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: '10px 18px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: saving ? '#9ca3af' : '#2563eb',
                color: '#fff',
                fontWeight: 700,
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Saving…' : 'Save fee settings'}
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={handlePauseAllFees}
              style={{
                padding: '10px 18px',
                borderRadius: '8px',
                border: '1px solid #f59e0b',
                backgroundColor: '#fffbeb',
                color: '#92400e',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Pause all fees (0%)
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={handleRestoreDefaultFee}
              style={{
                padding: '10px 18px',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                backgroundColor: '#f9fafb',
                color: '#374151',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Restore {PLATFORM_FEE_PERCENT}% + clear promo
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={handleClearPromo}
              style={{
                padding: '10px 18px',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                backgroundColor: '#fff',
                color: '#374151',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Clear timed promo only
            </button>
          </div>
        </form>

        <div
          style={{
            marginTop: '24px',
            paddingTop: '20px',
            borderTop: '1px solid #e5e7eb',
            display: 'grid',
            gap: '10px',
            maxWidth: '520px',
          }}
        >
          <strong>Manual free-for-life (by user ID)</strong>
          <p style={{ margin: 0, color: '#6b7280', fontSize: '0.88rem', lineHeight: 1.5 }}>
            Firebase Auth UID of the owner. Find it in Firebase Console → Authentication, or in Firestore{' '}
            <code>users</code> document IDs.
          </p>
          <input
            type="text"
            placeholder="User UID"
            value={exemptUserId}
            onChange={(e) => setExemptUserId(e.target.value)}
            style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db' }}
          />
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              type="button"
              disabled={exemptBusy}
              onClick={() => handleExemptToggle(true)}
              style={{
                padding: '10px 16px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#047857',
                color: '#fff',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Grant 100% free-for-life
            </button>
            <button
              type="button"
              disabled={exemptBusy}
              onClick={() => handleExemptToggle(false)}
              style={{
                padding: '10px 16px',
                borderRadius: '8px',
                border: '1px solid #ef4444',
                backgroundColor: '#fef2f2',
                color: '#b91c1c',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Revoke free-for-life
            </button>
          </div>
        </div>

        {message && (
          <p style={{ margin: '16px 0 0', color: '#1e40af', fontSize: '0.92rem', lineHeight: 1.5 }}>{message}</p>
        )}
      </div>
    </section>
  );
}

function RetryPayoutsCard({ rentals = [], onUpdated }) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [rentalId, setRentalId] = useState('');

  const failedOrHeld = (rentals || []).filter(
    (r) =>
      (r.paymentStatus === 'paid' || r.paid) &&
      r.payoutStatus &&
      r.payoutStatus !== 'transferred' &&
      r.payoutStatus !== 'cancelled_refunded'
  );

  const handleRetry = async (specificId = '') => {
    const id = (specificId || rentalId).trim();
    if (id) {
      // ok
    } else if (
      !window.confirm(
        'Retry all paid rentals that still need an owner payout (failed, held, or pending Connect)? Make sure the business Stripe available balance can cover transfers.'
      )
    ) {
      return;
    }

    setBusy(true);
    setMessage('');
    try {
      const retry = httpsCallable(functions, 'retryFailedRentalPayouts');
      const result = await retry(id ? { rentalId: id } : {});
      const data = result.data || {};
      setMessage(data.message || 'Payout retry finished.');
      onUpdated?.();
    } catch (error) {
      setMessage(error.message || 'Payout retry failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section style={{ marginBottom: '36px' }}>
      <h3 style={{ color: '#1f2937', marginBottom: '16px' }}>Owner payouts</h3>
      <div style={{ ...cardStyle }}>
        <p style={{ margin: '0 0 14px', color: '#6b7280', lineHeight: 1.55, fontSize: '0.95rem' }}>
          Use this after topping up the business Stripe account if a transfer failed for low funds. The system also
          retries automatically about every hour. Paid rentals not yet transferred: <strong>{failedOrHeld.length}</strong>
          .
        </p>

        {failedOrHeld.length > 0 && (
          <div style={{ display: 'grid', gap: '8px', marginBottom: '16px' }}>
            {failedOrHeld.slice(0, 15).map((rental) => (
              <div
                key={`retry-${rental.id}`}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '10px',
                  padding: '12px 14px',
                  borderRadius: '10px',
                  backgroundColor: rental.payoutStatus === 'payout_failed' ? '#fef2f2' : '#f9fafb',
                  border: '1px solid #e5e7eb',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <strong>{rental.gearName || rental.id}</strong>
                  <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '0.88rem' }}>
                    {rental.payoutStatus || '—'}
                    {rental.payoutError ? ` · ${rental.payoutError}` : ''}
                    {formatPayoutDueLabel(rental) ? ` · due ${formatPayoutDueLabel(rental)}` : ''}
                  </p>
                  <p style={{ margin: '2px 0 0', color: '#9ca3af', fontSize: '0.8rem' }}>ID: {rental.id}</p>
                </div>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => handleRetry(rental.id)}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: busy ? '#9ca3af' : '#2563eb',
                    color: '#fff',
                    fontWeight: 700,
                    cursor: busy ? 'not-allowed' : 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Retry this payout
                </button>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'grid', gap: '10px', maxWidth: '520px' }}>
          <label style={{ display: 'grid', gap: '6px', fontWeight: 600, fontSize: '0.9rem' }}>
            Optional rental ID (leave blank to retry all due)
            <input
              type="text"
              placeholder="Firestore rentals document ID"
              value={rentalId}
              onChange={(e) => setRentalId(e.target.value)}
              style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db' }}
            />
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            <button
              type="button"
              disabled={busy}
              onClick={() => handleRetry()}
              style={{
                padding: '12px 18px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: busy ? '#9ca3af' : '#0f766e',
                color: '#fff',
                fontWeight: 700,
                cursor: busy ? 'not-allowed' : 'pointer',
              }}
            >
              {busy ? 'Retrying…' : rentalId.trim() ? 'Retry this rental payout' : 'Retry all failed / due payouts'}
            </button>
          </div>
        </div>

        {message && (
          <p style={{ margin: '14px 0 0', color: '#1e40af', fontSize: '0.92rem', lineHeight: 1.5 }}>{message}</p>
        )}
      </div>
    </section>
  );
}

function RefundsCard({ rentals = [], onUpdated }) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [rentalId, setRentalId] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  const needsReview = (rentals || []).filter(
    (r) => r.refundStatus === 'manual_required'
  );

  const issueRefund = async ({ id, amountValue, reasonValue, confirmText }) => {
    if (confirmText && !window.confirm(confirmText)) return;

    setBusy(true);
    setMessage('');
    try {
      const refund = httpsCallable(functions, 'refundRentalPayment');
      const payload = { rentalId: id, reason: reasonValue || '' };
      const parsed = Number(amountValue);
      if (Number.isFinite(parsed) && parsed > 0) payload.amount = parsed;

      const result = await refund(payload);
      setMessage(result.data?.message || 'Refund issued.');
      setRentalId('');
      setAmount('');
      setReason('');
      onUpdated?.();
    } catch (error) {
      setMessage(error.message || 'Refund failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section style={{ marginBottom: '36px' }}>
      <h3 style={{ color: '#1f2937', marginBottom: '16px' }}>
        Refunds{needsReview.length > 0 ? ` (${needsReview.length} need review)` : ''}
      </h3>
      <div style={{ ...cardStyle }}>
        <p style={{ margin: '0 0 14px', color: '#6b7280', lineHeight: 1.55, fontSize: '0.95rem' }}>
          Refunds happen automatically when a booking is cancelled early, or when an owner cancels
          inside the 48-hour window. Use this for goodwill refunds an owner and renter agreed on, or
          for anything the system flagged for review. If the owner was already paid, the money is
          pulled back out of their Stripe account.
        </p>

        {needsReview.length > 0 && (
          <div style={{ display: 'grid', gap: '8px', marginBottom: '16px' }}>
            {needsReview.map((rental) => (
              <div
                key={`refund-${rental.id}`}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '10px',
                  padding: '12px 14px',
                  borderRadius: '10px',
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <strong>{rental.gearName || rental.id}</strong>
                  <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '0.88rem' }}>
                    Cancelled by {rental.cancelledByRole || 'unknown'} · paid $
                    {(Number(rental.totalPaidCents || 0) / 100).toFixed(2)} · payout{' '}
                    {rental.payoutStatus || '—'}
                  </p>
                  <p style={{ margin: '2px 0 0', color: '#9ca3af', fontSize: '0.8rem' }}>ID: {rental.id}</p>
                </div>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    issueRefund({
                      id: rental.id,
                      reasonValue: 'Admin review of cancelled rental',
                      confirmText: `Refund the full $${(
                        Number(rental.totalPaidCents || 0) / 100
                      ).toFixed(2)} for "${rental.gearName || rental.id}"?`,
                    })
                  }
                  style={{
                    padding: '8px 14px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: busy ? '#9ca3af' : '#b91c1c',
                    color: '#fff',
                    fontWeight: 700,
                    cursor: busy ? 'not-allowed' : 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Refund in full
                </button>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'grid', gap: '10px', maxWidth: '520px' }}>
          <label style={{ display: 'grid', gap: '6px', fontWeight: 600, fontSize: '0.9rem' }}>
            Rental ID
            <input
              type="text"
              placeholder="Firestore rentals document ID"
              value={rentalId}
              onChange={(e) => setRentalId(e.target.value)}
              style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db' }}
            />
          </label>
          <label style={{ display: 'grid', gap: '6px', fontWeight: 600, fontSize: '0.9rem' }}>
            Amount in dollars (leave blank to refund everything remaining)
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder="e.g. 45.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db' }}
            />
          </label>
          <label style={{ display: 'grid', gap: '6px', fontWeight: 600, fontSize: '0.9rem' }}>
            Reason (kept on the rental record)
            <input
              type="text"
              placeholder="e.g. Owner agreed to refund after weather cancellation"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db' }}
            />
          </label>
          <div>
            <button
              type="button"
              disabled={busy || !rentalId.trim()}
              onClick={() =>
                issueRefund({
                  id: rentalId.trim(),
                  amountValue: amount,
                  reasonValue: reason,
                  confirmText: amount.trim()
                    ? `Refund $${Number(amount).toFixed(2)} on rental ${rentalId.trim()}?`
                    : `Refund the full remaining amount on rental ${rentalId.trim()}?`,
                })
              }
              style={{
                padding: '12px 18px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: busy || !rentalId.trim() ? '#9ca3af' : '#b91c1c',
                color: '#fff',
                fontWeight: 700,
                cursor: busy || !rentalId.trim() ? 'not-allowed' : 'pointer',
              }}
            >
              {busy ? 'Refunding…' : 'Issue refund'}
            </button>
          </div>
        </div>

        {message && (
          <p style={{ margin: '14px 0 0', color: '#1e40af', fontSize: '0.92rem', lineHeight: 1.5 }}>{message}</p>
        )}
      </div>
    </section>
  );
}

function AdminDashboard({ user, isAdmin }) {
  const [rentals, setRentals] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!user || !isAdmin) {
      setRentals([]);
      setDisputes([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [paidSnap, holdSnap, refundReviewSnap, disputesSnap] = await Promise.all([
        getDocs(query(collection(db, 'rentals'), where('paymentStatus', '==', 'paid'))),
        getDocs(query(collection(db, 'rentals'), where('conditionHoldStatus', '==', 'authorized'))),
        getDocs(query(collection(db, 'rentals'), where('refundStatus', '==', 'manual_required'))),
        getDocs(query(collection(db, 'disputes'), where('status', '==', 'open'))),
      ]);

      const rentalMap = new Map();
      paidSnap.docs.forEach((docSnap) => {
        rentalMap.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
      });
      holdSnap.docs.forEach((docSnap) => {
        rentalMap.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
      });
      refundReviewSnap.docs.forEach((docSnap) => {
        rentalMap.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
      });

      const mergedRentals = Array.from(rentalMap.values()).sort((a, b) => {
        const aDate = new Date(a.paidAt || a.updatedAt || 0).getTime();
        const bDate = new Date(b.paidAt || b.updatedAt || 0).getTime();
        return bDate - aDate;
      });

      setRentals(mergedRentals);
      setDisputes(disputesSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
    } catch (error) {
      console.error('Admin dashboard load error:', error);
    } finally {
      setLoading(false);
    }
  }, [user, isAdmin]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (!user) {
    return (
      <p style={{ textAlign: 'center', padding: '80px', color: '#6b7280' }}>
        Please log in to access admin tools.
      </p>
    );
  }

  if (!isAdmin) {
    return (
      <div style={{ maxWidth: '720px', margin: '40px auto', padding: '0 24px' }}>
        <div style={{ ...cardStyle, textAlign: 'center' }}>
          <h2 style={{ marginTop: 0 }}>Admin access required</h2>
          <p style={{ color: '#6b7280', lineHeight: '1.6' }}>
            This page is only available after the site owner enables admin access from the header.
          </p>
        </div>
      </div>
    );
  }

  const activeHolds = rentals.filter((rental) => rental.conditionHoldStatus === 'authorized');
  const holdHistory = rentals.filter((rental) =>
    ['captured', 'released', 'release_failed', 'failed'].includes(rental.conditionHoldStatus)
  );

  return (
    <div style={{ maxWidth: '1100px', margin: '40px auto', padding: '0 24px' }}>
      <h2 style={{ textAlign: 'center', marginBottom: '12px' }}>Admin Dashboard</h2>
      <p style={{ textAlign: 'center', color: '#6b7280', marginBottom: '32px', lineHeight: '1.6' }}>
        Manage platform fees, founding free-for-life listers, condition holds, payouts, and review concerns.
      </p>

      <PlatformFeeSettingsCard />

      {loading ? (
        <p style={{ textAlign: 'center' }}>Loading admin data...</p>
      ) : (
        <>
          <RetryPayoutsCard rentals={rentals} onUpdated={loadData} />

          <RefundsCard rentals={rentals} onUpdated={loadData} />

          <section style={{ marginBottom: '36px' }}>
            <h3 style={{ color: '#b45309', marginBottom: '16px' }}>
              Active condition holds ({activeHolds.length})
            </h3>
            {activeHolds.length > 0 ? (
              activeHolds.map((rental) => (
                <ConditionHoldCard key={rental.id} rental={rental} onUpdated={loadData} />
              ))
            ) : (
              <div style={{ ...cardStyle, color: '#6b7280' }}>
                No active holds right now. Holds appear here after a renter pays for a listing with a condition guarantee.
              </div>
            )}
          </section>

          <section style={{ marginBottom: '36px' }}>
            <h3 style={{ color: '#1f2937', marginBottom: '16px' }}>
              Hold history ({holdHistory.length})
            </h3>
            {holdHistory.length > 0 ? (
              holdHistory.slice(0, 20).map((rental) => (
                <ConditionHoldCard key={`history-${rental.id}`} rental={rental} onUpdated={loadData} />
              ))
            ) : (
              <div style={{ ...cardStyle, color: '#6b7280' }}>No completed hold actions yet.</div>
            )}
          </section>

          <section style={{ marginBottom: '36px' }}>
            <h3 style={{ color: '#1f2937', marginBottom: '16px' }}>
              Paid rentals overview ({rentals.length})
            </h3>
            <div style={{ ...cardStyle }}>
              {rentals.length > 0 ? (
                <div style={{ display: 'grid', gap: '12px' }}>
                  {rentals.slice(0, 30).map((rental) => (
                    <div
                      key={`paid-${rental.id}`}
                      style={{
                        padding: '14px 16px',
                        borderRadius: '10px',
                        backgroundColor: '#f9fafb',
                        border: '1px solid #e5e7eb',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                        <strong>{rental.gearName || rental.id}</strong>
                        <HoldStatusBadge status={rental.conditionHoldStatus || 'not_required'} />
                      </div>
                      <p style={{ margin: '8px 0 0 0', color: '#6b7280', fontSize: '0.9rem' }}>
                        Paid {formatMoney((rental.totalPaidCents || rental.totalAmount * 100) / 100)} ·
                        Payout: {rental.payoutStatus || '—'} ·
                        {formatRentalDateRange(rental.rentalStartDate, rental.rentalEndDate)}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ margin: 0, color: '#6b7280' }}>No paid rentals yet.</p>
              )}
            </div>
          </section>

          <section>
            <h3 style={{ color: '#1f2937', marginBottom: '16px' }}>
              Open review concerns ({disputes.length})
            </h3>
            {disputes.length > 0 ? (
              disputes.map((dispute) => (
                <div key={dispute.id} style={{ ...cardStyle, marginBottom: '16px' }}>
                  <p style={{ margin: '0 0 8px 0', fontWeight: '700' }}>
                    {dispute.gearName || 'Review concern'}
                  </p>
                  <p style={{ margin: '0 0 6px 0', color: '#374151' }}>
                    <strong>Concern:</strong> {dispute.reason}
                  </p>
                  <p style={{ margin: '0 0 6px 0', color: '#6b7280', fontSize: '0.9rem' }}>
                    Reporter: {dispute.reporterEmail || dispute.reporterId} · Rating: {dispute.rating}/5
                  </p>
                  <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem' }}>
                    Review comment: {dispute.comment || '(none)'}
                  </p>
                </div>
              ))
            ) : (
              <div style={{ ...cardStyle, color: '#6b7280' }}>No open review concerns.</div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

export default AdminDashboard;